import React, { useContext } from "react"
import { Link } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useQueryClient } from "react-query"
import { format } from "date-fns"
import { UserContext } from "../../components/HomeApp"
import Footer from "../../components/shared/Footer/Footer"
import Header from "../../components/shared/Header/Header"
import style from "../../components/shared/PageStyle.module.css"
import { useHotBits } from "../../hooks/useHotBits"
import { useTopShows } from "../../hooks/useTopShows"
import {
  useUserPlaylists,
  useFollowedPlaylists,
  type UserPlaylist,
} from "../../hooks/useUserPlaylists"
import { PLAYLIST_PLACEHOLDER_IMAGE_URL } from "../../util/constants"
import { prependAndPlay } from "../../stores/mediaPlayerSlice"
import { sortedSongs } from "../../components/posts/PlaylistListItem"
import { request } from "../../util/request"
import { ExploreSection } from "./ExploreSection"
import { ExploreCard } from "./ExploreCard"

const HOT_BACKGROUND_URL =
  "https://bandnada.s3.amazonaws.com/static/external_assets/web/discover_hot_background.jpg"

const pageBackgroundStyle = {
  backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5)), url(${HOT_BACKGROUND_URL})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundAttachment: "fixed" as const,
  backgroundRepeat: "no-repeat" as const,
}

const playlistImage = (pl: UserPlaylist): string =>
  pl.post?.external_photo_src ||
  pl.post?.cover_image_url_small ||
  pl.post?.cover_image_url ||
  PLAYLIST_PLACEHOLDER_IMAGE_URL

// Matches the shape HotListsPlaylist/HotWaxAlbum/HotBitsSong already build
// for prependAndPlay, so a song can come from any of these rails and play
// the same way the existing Hot Bits carousel does.
const mapSongForPlayback = (song: any, contextAttrs: Record<string, any>) => ({
  id: song.post_id,
  title: song.title,
  band_id: song.band_id,
  band_name: song.band_name,
  album_id: song.album_id,
  album_name: song.album_name,
  album_path: song.album_path,
  cover_image_small: song.cover_image_small,
  duration: song.duration,
  local_path: song.local_path,
  nada_path: song.nada_path,
  parent_path: song.nada_path,
  streaming_url: song.streaming_url,
  post_type: song.post_type,
  ...contextAttrs,
})

interface NextShow {
  date: string
  nada_path: string
}

const BandExtrasDetails: React.FC<{
  bio?: string | null
  genre?: string | null
  nextShow?: NextShow | null
}> = ({ bio, genre, nextShow }) => {
  const thirdLine = bio || genre

  return (
    <>
      {thirdLine && (
        <div
          className="truncate text-[11px] text-white/80 mt-0.5"
          title={thirdLine}
        >
          {thirdLine}
        </div>
      )}
      {nextShow && (
        <Link
          to={nextShow.nada_path}
          className="block text-[11px] text-white/80 mt-0.5 hover:underline"
        >
          Playing {format(new Date(nextShow.date), "MM/dd/yyyy")}
        </Link>
      )}
    </>
  )
}

const DiscoverPage = () => {
  const { user, scene } = useContext(UserContext)
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const { data: hotData, isLoading: hotLoading } = useHotBits(scene)
  const { data: topShowsData, isLoading: topShowsLoading } =
    useTopShows(scene)
  const { data: createdData, isLoading: createdLoading } = useUserPlaylists(
    user?.id,
  )
  const { data: followedData, isLoading: followedLoading } =
    useFollowedPlaylists(user?.id)

  const hotBitsEnabled = hotLoading || hotData?.enabled

  // My Lists / Lists I Follow don't come back with embedded songs (unlike
  // Hot Lists/Wax/Bits), so fetch them on demand when play is actually
  // clicked, same approach as UserPlaylists.tsx's PlaylistRow.
  const playUserPlaylist = async (pl: UserPlaylist) => {
    const postId = pl.post_id ?? pl.post?.id
    if (!postId) return
    const data: any = await queryClient.fetchQuery(
      ["post", postId, "songs", null],
      () =>
        request({
          queryKey: [
            "post",
            { path: `/api/v1/posts/${postId}.json`, params: { fields: "songs" } },
          ],
        }),
    )
    const songs = sortedSongs(data?.post, pl)
    if (songs.length) dispatch(prependAndPlay(songs))
  }

  return (
    <div className={style.rootElStyle} style={pageBackgroundStyle}>
      <Header user={user} />

      {hotBitsEnabled && (
        <>
          <ExploreSection
            title="Hot Lists"
            description="Popular playlists on bandNada right now"
            light
            items={hotData?.playlists}
            isLoading={hotLoading}
            keyExtractor={(pl: any) => pl.playlist_id}
            renderItem={(pl: any) => (
              <ExploreCard
                imageUrl={
                  pl.cover_image_small ||
                  pl.songs?.[0]?.cover_image_small ||
                  PLAYLIST_PLACEHOLDER_IMAGE_URL
                }
                title={pl.name}
                subtitle={pl.band_name}
                hoverDescription={pl.description}
                light
                details={
                  <div className="text-[11px] text-white/80 mt-0.5">
                    {pl.song_count} song{pl.song_count === 1 ? "" : "s"}
                  </div>
                }
                onPlayClick={() =>
                  pl.songs?.length &&
                  dispatch(
                    prependAndPlay(
                      pl.songs.map((s: any) =>
                        mapSongForPlayback(s, {
                          playlistId: hotData?.hot_lists_playlist_id ?? null,
                          playlist_name: "Hot Lists",
                          playlist_path: hotData?.hot_lists_playlist_path ?? null,
                        }),
                      ),
                    ),
                  )
                }
                to={pl.nada_path}
              />
            )}
          />

          <ExploreSection
            title="Hot Wax"
            description="Popular albums on bandNada right now"
            light
            items={hotData?.albums}
            isLoading={hotLoading}
            keyExtractor={(album: any) => album.album_id}
            renderItem={(album: any) => (
              <ExploreCard
                imageUrl={
                  album.cover_image_small || PLAYLIST_PLACEHOLDER_IMAGE_URL
                }
                title={album.album_name}
                subtitle={album.band_name}
                light
                details={
                  <BandExtrasDetails
                    bio={album.band_bio}
                    genre={album.band_genre}
                    nextShow={album.next_show}
                  />
                }
                onPlayClick={() =>
                  album.songs?.length &&
                  dispatch(
                    prependAndPlay(
                      album.songs.map((s: any) =>
                        mapSongForPlayback(s, {
                          playlistId: hotData?.hot_wax_playlist_id ?? null,
                          playlist_name: "Hot Wax",
                          playlist_path: hotData?.hot_wax_playlist_path ?? null,
                        }),
                      ),
                    ),
                  )
                }
                to={album.album_path}
              />
            )}
          />

          <ExploreSection
            title="Hot Bits"
            description="Popular Songs on bandNada right now"
            light
            items={hotData?.songs}
            isLoading={hotLoading}
            keyExtractor={(song: any) => song.post_id}
            renderItem={(song: any) => (
              <ExploreCard
                imageUrl={
                  song.cover_image_small ||
                  song.band_cover_image ||
                  PLAYLIST_PLACEHOLDER_IMAGE_URL
                }
                title={song.title}
                subtitle={song.band_name}
                light
                details={
                  <BandExtrasDetails
                    bio={song.band_bio}
                    genre={song.band_genre}
                    nextShow={song.next_show}
                  />
                }
                onPlayClick={() =>
                  dispatch(
                    prependAndPlay([
                      mapSongForPlayback(song, {
                        playlistId: hotData?.hot_bits_playlist_id ?? null,
                        playlist_name: "Hot Bits",
                        playlist_path: hotData?.hot_bits_playlist_path ?? null,
                      }),
                    ]),
                  )
                }
                to={song.nada_path}
              />
            )}
          />
        </>
      )}

      <ExploreSection
        title="Top Upcoming Shows"
        light
        items={topShowsData?.shows}
        isLoading={topShowsLoading}
        keyExtractor={(show) => show.id}
        renderItem={(show) => (
          <ExploreCard
            imageUrl={show.image_url || PLAYLIST_PLACEHOLDER_IMAGE_URL}
            title={show.title}
            subtitle={`${format(new Date(show.date), "MMM d")} ${
              show.venue_city ?? ""
            }`.trim()}
            light
            to={show.nada_path}
          />
        )}
      />

      {user && (
        <>
          <ExploreSection
            title="My Lists"
            light
            items={(createdData as any)?.playlists}
            isLoading={createdLoading}
            keyExtractor={(pl: UserPlaylist) => pl.id}
            renderItem={(pl: UserPlaylist) => (
              <ExploreCard
                imageUrl={playlistImage(pl)}
                title={pl.name}
                subtitle={`${pl.playlist_posts_count} song${
                  pl.playlist_posts_count === 1 ? "" : "s"
                }`}
                hoverDescription={pl.description}
                light
                onPlayClick={() => playUserPlaylist(pl)}
                to={pl.nada_path}
              />
            )}
          />

          <ExploreSection
            title="Lists I Follow"
            light
            items={(followedData as any)?.playlists}
            isLoading={followedLoading}
            keyExtractor={(pl: UserPlaylist) => pl.id}
            renderItem={(pl: UserPlaylist) => (
              <ExploreCard
                imageUrl={playlistImage(pl)}
                title={pl.name}
                subtitle={`by ${pl.user_nickname}`}
                hoverDescription={pl.description}
                light
                onPlayClick={() => playUserPlaylist(pl)}
                to={pl.nada_path}
              />
            )}
          />
        </>
      )}

      <Footer />
    </div>
  )
}

export default DiscoverPage
